import React, { useEffect, useMemo, useState } from "react";
import { Col, Dropdown, Form, OverlayTrigger, Row, Spinner, Toast, ToastContainer, Tooltip } from "react-bootstrap";
import ReactCountryFlag from "react-country-flag";
import { Link, useNavigate } from "react-router-dom";

import DstAlert from "../../../../components/DstAlert/DstAlert";
import DstButton from "../../../../components/DstButton/DstButton";
import DstConfirm from "../../../../components/DstConfirm/DstConfirm";
import DstTable from "../../../../components/DstTable/DstTable";
import DstTableSearch from "../../../../components/DstTable/DstTableSearch/DstTableSearch";
import DstTabs from "../../../../components/DstTabs/DstTabs";
import InfoRow from "../../../../components/InfoRow/InfoRow";
import Lister from "../../../../components/Lister/Lister";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import Popup from "../../../../components/Popup/Popup";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { useSession } from "../../../../contexts/SessionContext";
import { PermissionComponent, validateUserPermission } from "../../../../services/PermissionGuard";
import { formatDateHumanly, formatToISODateTime, getAgeHumanly, isPast } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { completeNavigate } from "../../../../services/UtilityService";
import { MeetingType } from "../../../MeetingTypes/MeetingTypes.d";
import { Animator, MeetingLinkService } from "../../Meeting.d";
import {
  addNoteToMeeting,
  deleteMeeting,
  deleteNote,
  fetchMeetingNotes,
  fetchMeetingTypes,
  updateAnimators,
  updateMeeting,
  updateMeetingRecords,
  updateNote,
} from "../../Meeting.function";
import CohortsAttendancePopup from "./CohortsAttendancePopup/CohortsAttendancePopup";
import {
  copyMeetingLink,
  copyRecordLinkMessage,
  getAnimators,
  openRecordLink,
  removeCohortsFromAttendance,
  removeUsersFromAttendance,
  sendAnimatorsInvitations,
} from "./ManageMeeting.function";
import "./ManageMeeting.scss";
import MeetingReminderPopup from "./ReminderTemplatePopup/ReminderTemplate";
import UsersAttendancePopup from "./UsersAttendancePopup/UsersAttendancePopup";

const ManageMeeting = ({ meeting, refreshMeeting }: { meeting: any; refreshMeeting: Function }) => {
  const { language, email, permissions } = useSession();
  const navigate = useNavigate();

  const [note, setNote] = useState<string>("");
  const [showDescription, setShowDescription] = useState(true);
  // const [showOverview, setshowOverview] = useState(true);
  const [showNotes, setShowNotes] = useState(true);
  const [activeTab, setActiveTab] = useState<string>("cohorts");
  const [showAllCohorts, setShowAllCohorts] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDetailedSearch, setShowDetailedSearch] = useState(false);
  const [filteredCohorts, setFilteredCohorts] = useState(meeting.cohorts);
  const [filteredUsers, setFilteredUsers] = useState(meeting.attendees);
  const [showEditModal, setShowEditModal] = useState(false);
  const [animators, setAnimators] = useState([]);
  const [showAnimatorModal, setShowAnimatorModal] = useState(false);
  const [selectedHost, setSelectedHost] = useState<Animator | null>(
    meeting.animators?.find((a: Animator) => a.is_host) ?? null
  );
  const [meetingLinkService, setMeetingLinkService] = useState<MeetingLinkService>(
    meeting.author.full_name === "Tech AWAY" ? null : meeting.service ?? null
  );
  const [loading, setLoading] = useState({
    animators: true,
    projects: true,
  });
  const [submittingUpdate, setSubmittingUpdate] = useState(false);
  const [isUpdatingAnimators, setIsUpdatingAnimators] = useState(false);
  const [notes, setNotes] = useState<any[]>([]);
  const [loadingNotes, setLoadingNotes] = useState<boolean>(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<string>("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  const [selectedUsersIds, setSelectedUsersIds] = useState<number[]>([]);
  const [selectedCohortsIds, setSelectedCohortsIds] = useState<number[]>([]);
  const [openAddUsersToAttendanceModal, setOpenAddUsersToAttendanceModal] = useState(false);
  const [openAddCohortsToAttendanceModal, setOpenAddCohortsToAttendanceModal] = useState(false);
  const [isUsersRemovingFromAttendance, setIsUsersRemovingFromAttendance] = useState(false);
  const [isCohortsRemovingFromAttendance, setIsCohortsRemovingFromAttendance] = useState(false);
  const [openEditRecordsModal, setOpenEditRecordsModal] = useState(false);
  const [recordPassword, setRecordPassword] = useState("");
  const [recordLink, setRecordLink] = useState("");
  const [isMeetingRecordsUpdating, setIsMeetingRecordsUpdating] = useState(false);
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [loadingMeetingTypes, setLoadingMeetingTypes] = useState(false);
  const [selectedProject, setSelectedProject] = useState<{ id: number; name: string } | null>(meeting.project || null);
  const [showCopyMeetingLinkToast, setShowCopyMeetingLinkToast] = useState(false);
  const [showCopyRecordToast, setShowCopyRecordToast] = useState(false);
  const [selectedAnimators, setSelectedAnimators] = useState<Animator[]>(meeting.animators);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [showReminderPopup, setShowReminderPopup] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertProps, setAlertProps] = useState<any>({});
  const [showCopyEmailsToast, setShowCopyEmailsToast] = useState(false);
  const [isSendingIcs, setIsSendingIcs] = useState<boolean>(false);
  const [showSendIcsSuccessToast, setShowSendIcsSuccessToast] = useState<boolean>(false);

  const displayAlert = (props: any) => {
    setAlertProps(props);
    setShowAlert(true);
  };

  const _buildFormFromMeeting = (meeting: any) => ({
    dt_start: formatToISODateTime(meeting.dt_start, true).replace(" ", "T"),
    dt_end: formatToISODateTime(meeting.dt_end, true).replace(" ", "T"),
    name: meeting.name,
    visibility: meeting.visibility || "",
    type_id: meeting.type.id || "",
    description: meeting.description || "",
    url: meeting.url || "",
    project_id: meeting.project?.id || null,
  });
  const [formData, setFormData] = useState(_buildFormFromMeeting(meeting));
  const isEdited = useMemo(
    () => JSON.stringify(_buildFormFromMeeting(meeting)) !== JSON.stringify(formData),
    [meeting, formData]
  );

  const MAX_VISIBLE_ROWS = 3;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  useEffect(() => {
    getAnimators(setAnimators, setLoading, language);
  }, [language, permissions]);

  useEffect(() => {
    if (showEditModal) {
      fetchMeetingTypes(setMeetingTypes, setLoadingMeetingTypes, language);
    }
  }, [showEditModal, language]);

  const allAnimators = useMemo(() => [...selectedAnimators, ...selectedItems], [selectedAnimators, selectedItems]);

  useEffect(() => {
    if (meetingLinkService === null) {
      setSelectedHost(null);
    } else if (allAnimators.length >= 1) {
      setSelectedHost((prev) => prev ?? allAnimators[0]);
    }
  }, [allAnimators, meetingLinkService]);

  const getCohortColumns = () => {
    const baseColumns = [
      {
        key: "name",
        name: translate(language, "GLOBAL.NAME"),
      },
      {
        key: "cohort_leader",
        name: translate(language, "GLOBAL.COHORT_LEADER"),
        render: (row: any) =>
          row.cohort_leader ? (
            <span
              className="clickable"
              onClick={(event) => completeNavigate(event, navigate, `/user/${row.cohort_leader.id}`)}
            >
              {row.cohort_leader.full_name}
            </span>
          ) : (
            <span className="text-muted">N/A</span>
          ),
      },
      {
        key: "program_manager",
        name: translate(language, "GLOBAL.PROGRAM_MANAGER"),
        render: (row: any) =>
          row.program_manager ? (
            <span
              className="clickable"
              onClick={(event) => completeNavigate(event, navigate, `/user/${row.program_manager.id}`)}
            >
              {row.program_manager.full_name}
            </span>
          ) : (
            <span className="text-muted">N/A</span>
          ),
      },
    ];
    if (showDetailedSearch) {
      const detailedColumns = [
        {
          key: "description",
          name: translate(language, "GLOBAL.DESCRIPTION"),
          render: (row: any) =>
            row.description ? (
              row.description.startsWith("<>") ? (
                row.description.slice(2)
              ) : (
                row.description
              )
            ) : (
              <span className="text-muted">N/A</span>
            ),
        },
      ];
      return [...baseColumns, ...detailedColumns];
    }

    return baseColumns;
  };

  const userColumns = [
    {
      key: "full_name",
      name: translate(language, "GLOBAL.NAME"),
      render: (row: any) => row.full_name,
    },
    {
      key: "email",
      name: translate(language, "GLOBAL.EMAIL"),
      render: (row: any) => row.email,
    },
    {
      key: "badges",
      name: translate(language, "GLOBAL.FLAGS"),
      render: (row: any) =>
        row.flags.length
          ? row.flags.map((flag: any, index: number) => (
              <span
                key={`${flag.name}-${index}`}
                title={flag.name}
                className={`blue-font dst-icon-${flag.icon_name}`}
              />
            ))
          : translate(language, "PAGES.MEETING.MANAGE_MEETING.USER_COLUMN.FLAGS.NONE"),
      sortCompute: (row: any) => row.flags.length,
    },
  ];

  const tabsData = [
    {
      id: "cohorts",
      title: translate(language, "PAGES.MEETING.MANAGE_MEETING.TABS.COHORTS.TITLE"),
      content: (
        <React.Fragment>
          <DstTable
            columns={getCohortColumns()}
            data={showAllCohorts ? filteredCohorts : filteredCohorts.slice(0, MAX_VISIBLE_ROWS)}
            onRowClick={(event, row) => completeNavigate(event, navigate, `/cohort/${row.id}`)}
            onRowSelect={(selectedRows) => setSelectedCohortsIds(selectedRows.map((row: any) => row.id))}
            selectedItems={filteredCohorts.filter((cohort: any) => selectedCohortsIds.includes(cohort.id))}
            selectable={validateUserPermission(
              [{ methods: ["DELETE"], route: "/meetings/*/attendance/cohorts" }],
              permissions
            )}
          />
          {filteredCohorts.length > MAX_VISIBLE_ROWS && (
            <DstButton
              value={
                showAllCohorts
                  ? translate(language, "PAGES.MEETING.MANAGE_MEETING.TABS.COHORTS.BUTTON.SEE_LESS")
                  : translate(language, "PAGES.MEETING.MANAGE_MEETING.TABS.COHORTS.BUTTON.VIEW_ALL_COHORTS").replace(
                      "{{REMAINING_COHORTS_COUNT}}",
                      (filteredCohorts.length - MAX_VISIBLE_ROWS).toString()
                    )
              }
              variant="light"
              btnClass="mt-2 w-100"
              clickFunction={() => setShowAllCohorts(!showAllCohorts)}
            />
          )}
        </React.Fragment>
      ),
    },
    {
      id: "users",
      title: "Users",
      content: (
        <React.Fragment>
          <DstTable
            columns={userColumns}
            data={showAllUsers ? filteredUsers : filteredUsers.slice(0, MAX_VISIBLE_ROWS)}
            onRowClick={(event, row) => completeNavigate(event, navigate, `/user/${row.id}`)}
            onRowSelect={(selectedRows) => setSelectedUsersIds(selectedRows.map((row: any) => row.id))}
            selectedItems={filteredUsers.filter((user: any) => selectedUsersIds.includes(user.id))}
            selectable={validateUserPermission(
              [{ methods: ["DELETE"], route: "/meetings/*/attendance/users" }],
              permissions
            )}
          />
          {filteredUsers.length > MAX_VISIBLE_ROWS && (
            <DstButton
              value={
                showAllUsers
                  ? translate(language, "PAGES.MEETING.MANAGE_MEETING.TABS.USERS.BUTTON.SEE_LESS")
                  : translate(language, "PAGES.MEETING.MANAGE_MEETING.TABS.USERS.BUTTON.VIEW_ALL_COHORTS").replace(
                      "{{TOTAL_USERS_COUNT}}",
                      filteredUsers.length.toString()
                    )
              }
              variant="light"
              btnClass="mt-2 w-100"
              clickFunction={() => setShowAllUsers(!showAllUsers)}
            />
          )}
        </React.Fragment>
      ),
    },
  ];

  const prepareSearchableCohorts = (data: any[]) => {
    return data.map((item) => ({
      ...item,
      cohort_leader_name: item.cohort_leader
        ? `${item.cohort_leader.first_name} ${item.cohort_leader.last_name}`.toLowerCase()
        : "n/a",
      program_manager_name: item.program_manager
        ? `${item.program_manager.first_name} ${item.program_manager.last_name}`.toLowerCase()
        : "n/a",
    }));
  };

  const prepareSearchableUsers = (data: any[]) => {
    return data.map((item) => ({
      ...item,
      user_full_name: item.full_name,
      user_email: item.email,
    }));
  };

  useEffect(() => {
    fetchMeetingNotes(meeting.id, language, setNotes, setLoadingNotes);
  }, [meeting.id, language]);

  const _copyAttendanceEmails = () => {
    const emails = filteredUsers
      .map((user: { email: string }) => user.email)
      .filter(Boolean)
      .join(", ");

    navigator.clipboard
      .writeText(emails)
      .then(() => setShowCopyEmailsToast(true))
      .catch((error) => {
        console.error("Failed to copy attendees emails:", error);
        alert(translate(language, "PAGES.MEETING.MANAGE_MEETING.ATTENDANCE.ALERTS.ERROR.COPY_EMAILS"));
      });
  };

  const _editButton = (
    <DstButton
      btnClass="me-2"
      btnImageBefore={<span className="dst-icon-edit-02 me-2" />}
      value={translate(language, "PAGES.MEETING.MANAGE_MEETING.BUTTON.EDIT")}
      clickFunction={() => setShowEditModal(true)}
      disabled={isPast(meeting.dt_end)}
    />
  );

  return (
    <div className="s-regular">
      <Row>
        <Col lg>
          {/* <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="m-bold mb-2">
              {translate(language, "PAGES.MEETING.MANAGE_MEETING.MEETING_STATUS_OVERVIEW.TITLE")}
            </div>
            <span
              className={`clickable dst-icon-chevron-${showOverview ? "up" : "down"}`}
              onClick={() => setshowOverview(!showOverview)}
            />
          </div>
          {showOverview && (
            <Row className="d-flex justify-content-between mx-0">
              <Col sm className="mb-2 mb-sm-0 status-card active p-3 me-3 rounded border">
                <div className="circle-outline mb-2">
                  <div className="circle-filled"></div>
                </div>
                <p className="mb-2">
                  {translate(language, "PAGES.MEETING.MANAGE_MEETING.MEETING_STATUS_OVERVIEW.MEETING_PLANNED")}
                </p>
                <DstButton
                  btnImageBefore={<span className="dst-icon-video-recorder me-2" />}
                  value={translate(language, "PAGES.MEETING.MANAGE_MEETING.MEETING_STATUS_OVERVIEW.JOIN_MEETING")}
                  btnClass="btn btn-primary"
                  link={meeting.meeting_link}
                />
              </Col>
              <Col sm className="mb-2 mb-sm-0 status-card p-3 me-3 rounded border">
                <div className="circle-outline mb-2">
                  <div className="circle-empty"></div>
                </div>
                <p className="mb-0">
                  {translate(language, "PAGES.MEETING.MANAGE_MEETING.MEETING_STATUS_OVERVIEW.ATTENDANCE_LOCKED")}
                </p>
              </Col>
              <Col sm className="mb-2 mb-sm-0 status-card p-3 rounded border">
                <div className="circle-outline mb-2">
                  <div className="circle-empty"></div>
                </div>
                <p className="mb-0">
                  {translate(language, "PAGES.MEETING.MANAGE_MEETING.MEETING_STATUS_OVERVIEW.MESSAGE_SENT")}
                </p>
              </Col>
            </Row>
          )} */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h3 className="mb-0 m-bold">{translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.TITLE")}</h3>
            <div className="d-flex">
              <PermissionComponent neededPermission={[{ methods: ["DELETE"], route: "/meetings/*" }]}>
                <DstButton
                  btnClass="me-2"
                  btnImageBefore={<span className="dst-icon-trash-01 me-2" />}
                  value={translate(language, "GLOBAL.DELETE")}
                  variant="danger"
                  clickFunction={() => setShowConfirm(true)}
                />
              </PermissionComponent>
              <DstConfirm
                show={showConfirm}
                onHide={() => {
                  setShowConfirm(false);
                }}
                onConfirm={() => {
                  deleteMeeting(meeting.id, language, setLoading, () => navigate("/meetings"));
                  setShowConfirm(false);
                }}
                title={translate(language, "PAGES.MEETING.MANAGE_MEETING.POPUP_DELETION.TITLE")}
                messageExplain={translate(language, "PAGES.MEETING.MANAGE_MEETING.POPUP_DELETION.EXPLAIN")}
                messageConfirm={translate(language, "PAGES.MEETING.MANAGE_MEETING.POPUP_DELETION.CONFIRM")}
                confirmTextButton={translate(language, "GLOBAL.DELETE")}
              />
              <PermissionComponent neededPermission={[{ methods: ["PUT"], route: "/meetings/*" }]}>
                {isPast(meeting.dt_end) ? (
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip>
                        {translate(language, "PAGES.MEETING.MANAGE_MEETING.BUTTON.EDIT_PAST_MEETING_TOOLTIP")}
                      </Tooltip>
                    }
                  >
                    <span className="d-inline-block" tabIndex={0}>
                      {_editButton}
                    </span>
                  </OverlayTrigger>
                ) : (
                  _editButton
                )}
              </PermissionComponent>
              <DstButton
                value={translate(language, "PAGES.MEETING.MANAGE_MEETING.BUTTON.REMINDER")}
                btnImageBefore={<span className="dst-icon-send-01 me-2" />}
                clickFunction={() => setShowReminderPopup(true)}
                variant="secondary"
              />
            </div>
          </div>
          {/* {InfoRow(
            "dst-icon-search-md",
            translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.POLE"),
            meeting.meeting_type.pole
          )} */}
          {InfoRow(
            "dst-icon-tag-01",
            translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.MEETING_TYPE_NAME"),
            meeting.type.name
          )}
          {meeting?.type.need_project &&
            meeting.project &&
            InfoRow(
              "dst-icon-grid-03",
              translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.MEETING_PROJECT"),
              <div
                className="clickable text-primary"
                onClick={(event: React.MouseEvent) =>
                  completeNavigate(event, navigate, `/project/${meeting.project.id}`)
                }
              >
                {meeting.project.name}
              </div>
            )}
          {InfoRow(
            "dst-icon-pencil-line",
            translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.MANDATORY_ATTENDANCE.TITLE"),
            meeting.edusign_locked ? translate(language, "GLOBAL.YES") : translate(language, "GLOBAL.NO")
          )}
          {InfoRow(
            "dst-icon-calendar-plus-01",
            translate(language, "GLOBAL.CREATED_DATE"),
            formatDateHumanly(language, meeting.dt_created, true)
          )}
          {InfoRow(
            "dst-icon-grid-03",
            translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.MEETING_ID"),
            meeting.id
          )}
          {InfoRow(
            "dst-icon-globe-05",
            translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.LANGUAGE_SPOKEN"),
            <div className="d-flex align-items-center gap-2">
              <ReactCountryFlag
                svg
                countryCode={meeting.language.split("-")[1]}
                title={meeting.language}
                className="country-flag"
              />
            </div>
          )}
          {InfoRow(
            "dst-icon-user-circle",
            translate(language, "GLOBAL.ORGANIZER"),
            <span
              className="text-primary clickable"
              onClick={(event) => {
                event.stopPropagation();
                completeNavigate(event, navigate, `/user/${meeting.author.id}`);
              }}
            >
              {meeting.author.full_name}
            </span>
          )}
          {InfoRow(
            "dst-icon-certificate-01",
            translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.PROGRAM"),
            meeting.program && (
              <span>
                {meeting.program.name}
                <span className="text-muted"> | {meeting.program.description}</span>
              </span>
            )
          )}
          {InfoRow(
            "dst-icon-link-01",
            translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.MEETING_LINK.TITLE"),
            meeting.service_infos?.join_url ? (
              <div className="text-end">
                <a href={meeting.service_infos.join_url} target="_blank" rel="noopener noreferrer">
                  {meeting.service_infos.join_url}
                </a>
              </div>
            ) : meeting.url ? (
              <div className="d-flex align-items-center text-end">
                <a href={meeting.url} target="_blank" rel="noopener noreferrer" title={meeting.url}>
                  {meeting.url.length > 80 ? `${meeting.url.substring(0, 80)}...` : meeting.url}
                </a>
                <span
                  className="dst-icon-copy-03 ms-2 clickable"
                  onClick={() => copyMeetingLink(meeting.url, language, setShowCopyMeetingLinkToast)}
                />
              </div>
            ) : (
              <span>N/A</span>
            )
          )}
          {meeting.service_infos?.recording_info?.share_url &&
            new Date(meeting.dt_end) < new Date() &&
            InfoRow(
              "dst-icon-film-03",
              translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.RECORD_LINK.TITLE"),
              <div>
                <span
                  className="link-primary clickable"
                  onClick={() => openRecordLink(meeting.service_infos?.recording_info, language)}
                >
                  {translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.RECORD_LINK.TEXT")}
                </span>
                <span
                  className="dst-icon-copy-03 ms-2 clickable"
                  onClick={() =>
                    copyRecordLinkMessage(meeting.service_infos?.recording_info, language, setShowCopyRecordToast)
                  }
                />

                <PermissionComponent neededPermission={[{ methods: ["PUT"], route: "/meetings/*/records" }]}>
                  <span
                    className="dst-icon-pencil-01 ms-2 clickable"
                    onClick={() => {
                      setOpenEditRecordsModal(true);
                      setRecordLink(meeting.service_infos?.recording_info?.share_url || "");
                      setRecordPassword(meeting.service_infos?.recording_info?.password || "");
                    }}
                  />
                </PermissionComponent>
              </div>
            )}
          {InfoRow(
            "dst-icon-eye-off",
            translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.VISIBILITY"),
            meeting.visibility
          )}
          {meeting?.campus_names?.length > 0 &&
            InfoRow(
              "dst-icon-building-07",
              translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.CAMPUS"),
              <div>
                {meeting.campus_names.map((campusName: string, index: number) => {
                  const campusLink = meeting.campus_links?.[index];
                  return (
                    <span key={index}>
                      {campusLink ? (
                        <a
                          href={campusLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary text-decoration-none clickable"
                        >
                          {campusName}
                        </a>
                      ) : (
                        campusName
                      )}
                      {index < meeting.campus_names.length - 1 && ", "}
                    </span>
                  );
                })}
              </div>
            )}
          {meeting?.id_ext &&
            InfoRow(
              "dst-icon-key-01",
              translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.EXTERNAL_ID"),
              meeting.id_ext
            )}
          <div className="d-flex align-items-center justify-content-between my-3">
            <h3 className="mb-0 m-bold">
              {translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.DESCRIPTION.TITLE")}
            </h3>
            <span
              className={`clickable dst-icon-chevron-${showDescription ? "up" : "down"}`}
              onClick={() => setShowDescription(!showDescription)}
            />
          </div>
          {showDescription && (
            <p className="mb-0 medium-gray-background p-3 rounded w-100 s-regular text-pre-wrap">
              {meeting.description}
            </p>
          )}
          <div className="d-flex align-items-center justify-content-between my-3">
            <h3 className="mb-0 m-bold">
              {translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.NOTE.TITLE")}
            </h3>
            <span
              className={`clickable dst-icon-chevron-${showNotes ? "up" : "down"}`}
              onClick={() => setShowNotes(!showNotes)}
            />
          </div>
          {showNotes && (
            <div>
              <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/meetings/*/notes" }]}>
                <div className="p-3 tinted-blue-background d-flex justify-content-between align-items-center mb-3 rounded">
                  <p className="mb-0 xs-regular">
                    {translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.NOTE.WARNING_MESSAGE")}
                  </p>
                </div>
                <div className="d-flex">
                  <Form.Control
                    as="textarea"
                    rows={1}
                    placeholder={translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.NOTE.PLACEHOLDER")}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="medium-gray-background s-regular"
                    style={{ resize: "none" }}
                  />
                  <DstButton
                    value={translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.NOTE.PUBLISH")}
                    btnClass="d-flex align-items-center ms-2"
                    clickFunction={() =>
                      addNoteToMeeting(meeting.id, note, language, setNote, setNotes, setLoadingNotes)
                    }
                    variant="secondary"
                    btnImageAfter={<span className="dst-icon-send-03 ms-2" />}
                  />
                </div>
              </PermissionComponent>
              {loadingNotes ? (
                <div className="mt-3 text-center">
                  <Spinner animation="border" size="sm" role="status" />
                </div>
              ) : (
                <div className="mt-3 scrollable" style={{ maxHeight: 300 }}>
                  {notes.length > 0 &&
                    notes
                      .sort((a, b) => new Date(b.dt_created).getTime() - new Date(a.dt_created).getTime())
                      .map((note) => (
                        <div key={note.id} className="note-item border rounded p-2 mb-2 s-regular">
                          {editingNoteId === note.id ? (
                            <React.Fragment>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={editedContent}
                                onChange={(e) => setEditedContent(e.target.value)}
                              />
                              <div className="d-flex justify-content-end mt-2">
                                <DstButton
                                  variant="primary"
                                  value={translate(
                                    language,
                                    "PAGES.MEETING.MANAGE_MEETING.INFORMATION.NOTE.BUTTONS.SAVE"
                                  )}
                                  clickFunction={() =>
                                    updateNote(
                                      meeting.id,
                                      note.id,
                                      editedContent,
                                      language,
                                      setEditingNoteId,
                                      setNotes,
                                      setLoadingNotes
                                    )
                                  }
                                  btnClass="me-2"
                                />
                                <DstButton
                                  variant="light"
                                  value={translate(language, "GLOBAL.CANCEL")}
                                  clickFunction={() => setEditingNoteId(null)}
                                />
                              </div>
                            </React.Fragment>
                          ) : (
                            <React.Fragment>
                              <div className="d-flex justify-content-between">
                                <p className="my-1">{note.content}</p>
                                {note.author === email && (
                                  <div className="my-1 ms-3">
                                    <span
                                      className="dst-icon-edit-02 clickable"
                                      onClick={() => {
                                        setEditingNoteId(note.id);
                                        setEditedContent(note.content);
                                      }}
                                      title={translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.NOTE.EDIT")}
                                    />
                                    <span
                                      className="dst-icon-trash-01 ms-2 text-danger clickable"
                                      onClick={() => {
                                        setNoteToDelete(note.id);
                                        setShowDeleteConfirm(true);
                                      }}
                                      title={translate(
                                        language,
                                        "PAGES.MEETING.MANAGE_MEETING.INFORMATION.NOTE.DELETE"
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="d-flex justify-content-between dark-gray-font">
                                <span>{note.author}</span>
                                <div>
                                  {getAgeHumanly(language, new Date(note.dt_created).getTime())}
                                  {note.dt_modified && (
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip id={`tooltip-note-${note.id}-edit-time`}>
                                          {getAgeHumanly(language, new Date(note.dt_modified).getTime())}
                                        </Tooltip>
                                      }
                                    >
                                      <span className="clickable ms-2">
                                        {translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.NOTE.EDITED")}
                                      </span>
                                    </OverlayTrigger>
                                  )}
                                </div>
                              </div>
                            </React.Fragment>
                          )}
                        </div>
                      ))}
                </div>
              )}
            </div>
          )}
        </Col>
        <Col className="mt-3 mt-lg-0">
          <div className="m-regular d-flex justify-content-between align-items-center mb-2">
            <div className="me-3">{translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.ANIMATORS")}</div>
            <div>
              {!isPast(meeting.dt_end) && (
                <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/meetings/*/animators/ics" }]}>
                  <DstButton
                    value={translate(language, "PAGES.MEETING.MANAGE_MEETING.BUTTON.SEND_ICS_ANIMATORS")}
                    variant="light"
                    btnImageBefore={
                      isSendingIcs ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                      ) : (
                        <span className="dst-icon-send-01 me-2" />
                      )
                    }
                    clickFunction={() =>
                      sendAnimatorsInvitations(meeting.id, language, setIsSendingIcs, () =>
                        setShowSendIcsSuccessToast(true)
                      )
                    }
                    disabled={isSendingIcs || !meeting.animators?.length}
                    btnClass="me-2"
                  />
                </PermissionComponent>
              )}
              <PermissionComponent neededPermission={[{ methods: ["PUT"], route: "/meetings/*/animators" }]}>
                <DstButton
                  value={translate(language, "PAGES.MEETING.MANAGE_MEETING.BUTTON.MANAGE_ANIMATOR")}
                  btnImageBefore={
                    isUpdatingAnimators ? (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                    ) : (
                      <span className="dst-icon-edit-02 me-2" />
                    )
                  }
                  clickFunction={() => setShowAnimatorModal(true)}
                  disabled={isUpdatingAnimators}
                />
              </PermissionComponent>
            </div>
          </div>
          <div className="mt-3 mt-lg-0">
            {meeting.animators?.length ? (
              <div className="d-inline-flex flex-wrap">
                {selectedAnimators.map((animator: Animator) => (
                  <div
                    key={animator.id}
                    className="animator-badge border border-primary text-primary rounded p-2 me-2 mb-2 d-flex align-items-center"
                  >
                    {animator?.is_host && (
                      <span
                        className="dst-icon-presentation-chart-01 me-2"
                        title={translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.HOST")}
                      />
                    )}
                    <span>{animator.full_name}</span>
                    <span className="border-start ms-2 ps-2">
                      <Link
                        to={`/meetings?animators=${animator.staff_member_id}&reset=true&period=30`}
                        className="d-inline-flex text-decoration-none"
                        title={translate(
                          language,
                          "PAGES.MEETING.MANAGE_MEETING.INFORMATION.ANIMATOR_MEETINGS"
                        ).replace("{{ANIMATOR}}", animator.full_name)}
                      >
                        <span className="dst-icon-calendar" />
                      </Link>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-danger">
                {translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.NO_ANIMATORS")}
              </span>
            )}
          </div>
          <Row className="d-flex mt-3 align-items-center">
            <Col className="m-regular">
              {translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.ATTENDEES")} ({meeting.attendees.length})
            </Col>
            <Col>
              <DstTableSearch
                data={
                  activeTab === "cohorts"
                    ? prepareSearchableCohorts(meeting.cohorts)
                    : prepareSearchableUsers(meeting.attendees)
                }
                searchableColumns={
                  activeTab === "cohorts"
                    ? ["name", "cohort_leader_name", "program_manager_name"]
                    : ["user_full_name", "user_email"]
                }
                onSearch={(results) => {
                  if (activeTab === "cohorts") {
                    setFilteredCohorts(results);
                  } else if (activeTab === "users") {
                    setFilteredUsers(results);
                  }
                }}
              />
            </Col>
          </Row>
          {activeTab === "users" && (
            <div className="d-flex justify-content-end align-items-center mt-3">
              {selectedUsersIds.length > 0 && (
                <DstButton
                  value={translate(language, "PAGES.MEETING.MANAGE_MEETING.ATTENDANCE.BUTTONS.REMOVE_USERS")}
                  btnClass="me-2"
                  variant="danger"
                  clickFunction={() => {
                    setIsUsersRemovingFromAttendance(true);
                    removeUsersFromAttendance(
                      meeting.id,
                      selectedUsersIds,
                      language,
                      () => {
                        refreshMeeting();
                        setSelectedUsersIds([]);
                      },
                      () => setIsUsersRemovingFromAttendance(false)
                    );
                  }}
                  loading={isUsersRemovingFromAttendance}
                />
              )}
              <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/meetings/*/attendance/users" }]}>
                <DstButton
                  value={translate(language, "PAGES.MEETING.MANAGE_MEETING.ATTENDANCE.BUTTONS.ADD_USERS")}
                  clickFunction={() => setOpenAddUsersToAttendanceModal(true)}
                />
              </PermissionComponent>

              {selectedUsersIds.length === 0 && (
                <DstButton
                  btnClass="ms-2"
                  value={translate(language, "PAGES.MEETING.MANAGE_MEETING.ATTENDANCE.BUTTONS.COPY_EMAILS")}
                  btnImageBefore={<span className="dst-icon-copy-03 me-2" />}
                  variant="secondary"
                  clickFunction={_copyAttendanceEmails}
                  disabled={!filteredUsers.length}
                />
              )}
            </div>
          )}
          {activeTab === "cohorts" && (
            <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
              {selectedCohortsIds.length > 0 && (
                <DstButton
                  value={translate(language, "PAGES.MEETING.MANAGE_MEETING.ATTENDANCE.BUTTONS.REMOVE_COHORTS")}
                  btnClass="me-2"
                  variant="danger"
                  clickFunction={() => {
                    setIsCohortsRemovingFromAttendance(true);
                    removeCohortsFromAttendance(
                      meeting.id,
                      selectedCohortsIds,
                      language,
                      () => {
                        refreshMeeting();
                        setSelectedCohortsIds([]);
                      },
                      () => setIsCohortsRemovingFromAttendance(false)
                    );
                  }}
                  loading={isCohortsRemovingFromAttendance}
                />
              )}
              <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/meetings/*/attendance/cohorts" }]}>
                <DstButton
                  value={translate(language, "PAGES.MEETING.MANAGE_MEETING.ATTENDANCE.BUTTONS.ADD_COHORTS")}
                  clickFunction={() => setOpenAddCohortsToAttendanceModal(true)}
                />
              </PermissionComponent>
              <DstButton
                btnClass="h-100 d-flex align-items-center"
                btnImageBefore={
                  <Form.Check
                    type="switch"
                    className="check-box s-bold me-2"
                    id="detailed-search-switch"
                    checked={showDetailedSearch}
                    onChange={() => setShowDetailedSearch(!showDetailedSearch)}
                  />
                }
                value={translate(language, "PAGES.MEETINGS.FILTERS.DETAILED_SEARCH")}
                variant="light"
                btnHeight="50px"
                btnWidth="150px"
                clickFunction={() => setShowDetailedSearch(!showDetailedSearch)}
              />
            </div>
          )}
          <DstTabs
            tabsData={tabsData}
            variant="primary"
            classes="mt-3"
            activeKey={activeTab}
            onSelect={(key) => setActiveTab(key || "cohorts")}
          />
          <CohortsAttendancePopup
            meetingId={meeting.id}
            language={language}
            openAddCohortsModal={openAddCohortsToAttendanceModal}
            setOpenAddCohortsModal={setOpenAddCohortsToAttendanceModal}
            refreshMeeting={refreshMeeting}
          />
          <UsersAttendancePopup
            meetingId={meeting.id}
            language={language}
            openAddUsersModal={openAddUsersToAttendanceModal}
            setOpenAddUsersModal={setOpenAddUsersToAttendanceModal}
            refreshMeeting={refreshMeeting}
          />

          {/* <h3 className="m-bold my-3">
            {translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.MEETING_HISTORY")}
          </h3>
          {meeting.meeting_history.map((historyItem: any, index: number) => (
            <div
              key={index}
              className="d-flex justify-content-between align-items-center border-bottom mb-3 pb-2 s-regular"
            >
              <div className="d-flex align-items-center">
                <span
                  className={`me-3 ${
                    historyItem.action === "removed"
                      ? "dst-icon-user-x-01 red-bright-font"
                      : "dst-icon-user-check-01 green-font"
                  }`}
                />
                <span>
                  <strong>{historyItem.actor}</strong>{" "}
                  <span className="text-muted">
                    {historyItem.action === "set" ? "set" : "removed"} <strong>{historyItem.affected_person}</strong> as{" "}
                    <strong>{historyItem.role}</strong>
                  </span>
                </span>
              </div>
              <div className="text-muted">{formatDateHumanly(language, historyItem.timestamp, true)}</div>
            </div>
          ))} */}
        </Col>
      </Row>
      <Popup
        show={showAnimatorModal}
        onClose={() => {
          setShowAnimatorModal(false);
          setSelectedItems([]);
        }}
        modalTitle={translate(language, "PAGES.MEETING.MANAGE_MEETING.BUTTON.MANAGE_ANIMATOR")}
      >
        <div className="d-flex flex-column">
          <MultiSelectDropdown
            showAlwaysConfirm
            mock={loading.animators}
            items={animators.filter(
              (animator: Animator) => !selectedAnimators.map((anim: Animator) => anim.id).includes(animator.id)
            )}
            displayItem={(item) => item.full_name}
            badgeItemProperty="full_name"
            onConfirm={(items: Animator[]) => setSelectedItems(items)}
            label={
              selectedItems.length > 0
                ? `${selectedItems.length} ${translate(
                    language,
                    "PAGES.MEETING.MANAGE_MEETING.POPUP_ANIMATORS.SELECTED_ANIMATORS"
                  )}`
                : translate(language, "PAGES.MEETING.MANAGE_MEETING.POPUP_ANIMATORS.SELECT_ANIMATOR")
            }
            language={language}
            hideCaret
            borderToggle
          />
          {[...selectedAnimators, ...selectedItems].length > 0 && (
            <div className="mt-4">
              <Lister
                columns={[
                  {
                    key: "staff_member_id",
                    header: translate(language, "GLOBAL.ID"),
                    sortable: false,
                  },
                  {
                    key: "email",
                    header: translate(language, "GLOBAL.EMAIL"),
                    sortable: false,
                    render: (item: any) => item.email,
                  },
                  {
                    key: "is_host",
                    header: translate(language, "PAGES.MEETING.MANAGE_MEETING.POPUP_ANIMATORS.COLUMNS.HOST"),
                    sortable: false,
                    render: (item: any) => (
                      <Form.Check
                        type="radio"
                        name="host_animator"
                        checked={selectedHost?.staff_member_id === item.staff_member_id}
                        onClick={() =>
                          setSelectedHost(selectedHost?.staff_member_id === item.staff_member_id ? null : item)
                        }
                        disabled={!meetingLinkService}
                      />
                    ),
                  },
                  {
                    key: "delete",
                    header: "",
                    sortable: false,
                    render: (item: any) => (
                      <span
                        className="dst-icon-x text-danger clickable"
                        onClick={() => {
                          setSelectedAnimators((prev) =>
                            prev.filter((animator) => animator.staff_member_id !== item.staff_member_id)
                          );
                          setSelectedItems((prev) =>
                            prev.filter((animator) => animator.staff_member_id !== item.staff_member_id)
                          );
                          if (selectedHost?.staff_member_id === item.staff_member_id) {
                            setSelectedHost(null);
                          }
                        }}
                      />
                    ),
                  },
                ]}
                items={[...selectedAnimators, ...selectedItems]}
              />
            </div>
          )}

          <div className="m-regular mt-4">
            {translate(language, "PAGES.MEETING.MANAGE_MEETING.POPUP_ANIMATORS.SERVICE_LINK_GENERATION_LABEL")}
          </div>
          <div className="mt-3 d-flex align-items-center">
            <Dropdown>
              <Dropdown.Toggle
                className="w-100 d-flex align-items-center border"
                variant=""
                disabled={meeting.author.full_name === "Tech AWAY"}
              >
                {meetingLinkService === "zoom" &&
                  translate(language, "PAGES.MEETING.MANAGE_MEETING.POPUP_ANIMATORS.SERVICE_LINK_GENERATION.ZOOM")}
                {meetingLinkService === "teams" &&
                  translate(language, "PAGES.MEETING.MANAGE_MEETING.POPUP_ANIMATORS.SERVICE_LINK_GENERATION.TEAMS")}
                {meetingLinkService === null &&
                  translate(language, "PAGES.MEETING.MANAGE_MEETING.POPUP_ANIMATORS.SERVICE_LINK_GENERATION.NONE")}
              </Dropdown.Toggle>
              <Dropdown.Menu className="w-100">
                <Dropdown.Item onClick={() => setMeetingLinkService("zoom")}>
                  {translate(language, "PAGES.MEETING.MANAGE_MEETING.POPUP_ANIMATORS.SERVICE_LINK_GENERATION.ZOOM")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setMeetingLinkService("teams")}>
                  {translate(language, "PAGES.MEETING.MANAGE_MEETING.POPUP_ANIMATORS.SERVICE_LINK_GENERATION.TEAMS")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setMeetingLinkService(null)}>
                  {translate(language, "PAGES.MEETING.MANAGE_MEETING.POPUP_ANIMATORS.SERVICE_LINK_GENERATION.NONE")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <div className="d-flex justify-content-end mt-4">
            <DstButton
              btnWidth="100px"
              variant="primary"
              value={translate(language, "GLOBAL.CONFIRM")}
              clickFunction={() => {
                updateAnimators({
                  meetingId: meeting.id,
                  updatedAnimators: [...selectedAnimators, ...selectedItems],
                  setSelectedAnimators,
                  meetingLinkService,
                  language,
                  setIsLoading: setIsUpdatingAnimators,
                  displayAlert,
                  successCallback: () => refreshMeeting(),
                  closeAlert: () => setShowAlert(false),
                  hostAnimatorId: selectedHost?.staff_member_id ?? undefined,
                });
                setSelectedItems([]);
                setShowAnimatorModal(false);
              }}
              btnClass="me-2"
              disabled={!!meetingLinkService && !selectedHost}
            />
          </div>
        </div>
      </Popup>
      <Popup
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        modalTitle={translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_POPUP.TITLE")}
        size="lg"
      >
        <Form>
          <Form.Group controlId="formMeetingName">
            <Form.Label>
              <RequiredLabel label={translate(language, "GLOBAL.NAME")} />
            </Form.Label>
            <Form.Control type="text" name="name" value={formData.name} onChange={handleInputChange} />
          </Form.Group>

          <Form.Group controlId="formMeetingDescription" className="mt-3">
            <Form.Label>{translate(language, "GLOBAL.DESCRIPTION")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
            />
          </Form.Group>

          <Form.Group controlId="formMeetingType" className="mt-3">
            <Form.Label>
              <RequiredLabel
                label={translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_POPUP.MEETING_TYPE.TITLE")}
              />
            </Form.Label>
            <MultiSelectDropdown
              items={meetingTypes.map((type) => ({
                ...type,
                id: type.id,
                searchable_text: type.name,
              }))}
              displayItem={(item: MeetingType) => {
                return <span>{item.name}</span>;
              }}
              badgeItemProperty="type_name"
              isSingleSelect
              language={language}
              onConfirm={(selectedItems: MeetingType[]) => {
                if (selectedItems.length > 0) {
                  setFormData((prevData) => ({
                    ...prevData,
                    type_id: selectedItems[0].id,
                  }));
                } else {
                  setFormData((prevData) => ({
                    ...prevData,
                    type_id: "",
                  }));
                }
              }}
              label={
                meetingTypes.find((type) => type.id === formData.type_id)?.name ||
                translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_POPUP.MEETING_TYPE.SELECT_TYPE")
              }
              toggleClass="w-100"
              borderToggle
              mock={loadingMeetingTypes}
              btnWidth="100%"
            />
          </Form.Group>
          {(meetingTypes.find((type) => type.id === formData.type_id)?.need_project ?? meeting?.type.need_project) && (
            <Form.Group className="mt-3">
              <Form.Label>
                <RequiredLabel label={translate(language, "PAGES.MEETINGS.MODAL.LABELS.PROJECT")} />
              </Form.Label>
              <MultiSelectLiveDropdown
                label={
                  selectedProject
                    ? `${translate(language, "PAGES.MEETINGS.MODAL.PLACEHOLDERS.PROJECT_SELECTED")} ${
                        selectedProject.name
                      }`
                    : translate(language, "PAGES.MEETINGS.MODAL.PLACEHOLDERS.SELECT_PROJECT")
                }
                category={["projects"]}
                language={language}
                isSingleSelect
                initialSelectedItems={selectedProject ? [{ category: "projects", value: selectedProject }] : []}
                onConfirm={(selected) => {
                  if (selected.length > 0) {
                    const projectId = +selected[0].value.id;
                    setFormData((prevData) => ({
                      ...prevData,
                      project_id: projectId,
                    }));
                    setSelectedProject({ id: projectId, name: selected[0].value.name });
                  } else {
                    setFormData((prevData) => ({
                      ...prevData,
                      project_id: null,
                    }));
                    setSelectedProject(null);
                  }
                }}
                toggleClass={`text-truncate w-100 border-secondary ${!selectedProject ? "text-muted" : ""}`}
              />
            </Form.Group>
          )}
          <Form.Group controlId="formStart" className="mt-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_POPUP.START_DATE")} />
            </Form.Label>
            <Form.Control
              type="datetime-local"
              name="dt_start"
              min={formatToISODateTime(new Date().toISOString(), true).replace(" ", "T")}
              value={formData.dt_start}
              onChange={handleInputChange}
            />
          </Form.Group>

          <Form.Group controlId="formEnd" className="mt-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_POPUP.END_DATE")} />
            </Form.Label>
            <Form.Control
              type="datetime-local"
              name="dt_end"
              min={formData.dt_start}
              value={formData.dt_end}
              onChange={handleInputChange}
            />
          </Form.Group>

          <Form.Group controlId="formVisibility" className="mt-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_POPUP.VISIBILITY.TITLE")} />
            </Form.Label>
            <Form.Select name="visibility" value={formData.visibility} onChange={handleInputChange}>
              <option value="shared">
                {translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_POPUP.VISIBILITY.SHARED")}
              </option>
              <option value="private">
                {translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_POPUP.VISIBILITY.PRIVATE")}
              </option>
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="formMeetingLink" className="mt-3">
            <Form.Label>{translate(language, "GLOBAL.LINK")}</Form.Label>
            <Form.Control type="text" name="url" value={formData.url} onChange={handleInputChange} />
          </Form.Group>

          <DstButton
            value={translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_POPUP.SAVE_CHANGES")}
            clickFunction={() =>
              updateMeeting(
                meeting.id,
                { ...formData },
                language,
                setShowEditModal,
                setSubmittingUpdate,
                refreshMeeting
              )
            }
            loading={submittingUpdate}
            disabled={
              !isEdited ||
              submittingUpdate ||
              isPast(formData.dt_start) ||
              new Date(formData.dt_end).getTime() <= new Date(formData.dt_start).getTime()
            }
            btnClass="mt-4"
          />
        </Form>
      </Popup>
      <Popup
        show={openEditRecordsModal}
        onClose={() => setOpenEditRecordsModal(false)}
        modalTitle={translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_ZOOM_RECORD.TITLES.MODAL_TITLE")}
      >
        <div className="d-flex flex-column">
          <div className="mt-2 d-flex flex-column">
            <label>{translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_ZOOM_RECORD.TITLES.LINK")}</label>
            <input
              type="text"
              value={recordLink}
              onChange={(event) => setRecordLink(event.target.value)}
              className="form-control mt-2"
              placeholder={translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_ZOOM_RECORD.PLACEHOLDERS.LINK")}
            />
          </div>
          <div className="mt-3 d-flex flex-column">
            <label>{translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_ZOOM_RECORD.TITLES.PASSWORD")}</label>
            <input
              type="text"
              value={recordPassword}
              onChange={(event) => setRecordPassword(event.target.value)}
              className="form-control mt-2"
              placeholder={translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_ZOOM_RECORD.PLACEHOLDERS.PASSWORD")}
            />
          </div>
          <div className="d-flex justify-content-end mt-4">
            <DstButton
              btnWidth="100px"
              variant="primary"
              value={translate(language, "GLOBAL.SAVE")}
              clickFunction={() => {
                updateMeetingRecords(
                  meeting.id,
                  recordPassword,
                  recordLink,
                  language,
                  setIsMeetingRecordsUpdating,
                  () => {
                    setOpenEditRecordsModal(false);
                    refreshMeeting();
                  }
                );
              }}
              btnClass="me-2"
              loading={isMeetingRecordsUpdating}
              disabled={isMeetingRecordsUpdating || !recordLink.trim() || !recordPassword.trim()}
            />
          </div>
        </div>
      </Popup>
      <DstConfirm
        show={showDeleteConfirm}
        onHide={() => {
          setShowDeleteConfirm(false);
          setNoteToDelete(null);
        }}
        onConfirm={() => {
          if (noteToDelete) {
            deleteNote(meeting.id, noteToDelete, language, setNotes, setLoadingNotes);
            setShowDeleteConfirm(false);
            setNoteToDelete(null);
          }
        }}
        title={translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.DELETE_NOTE_POPUP.TITLE")}
        messageExplain={translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.DELETE_NOTE_POPUP.EXPLAIN")}
        messageConfirm={translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.DELETE_NOTE_POPUP.CONFIRM")}
        confirmTextButton={translate(language, "GLOBAL.DELETE")}
      />
      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg="success"
          onClose={() => setShowCopyMeetingLinkToast(false)}
          show={showCopyMeetingLinkToast}
          delay={2000}
          autohide
        >
          <Toast.Body className="text-white">
            {translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.MEETING_LINK.COPY_LINK")}
          </Toast.Body>
        </Toast>

        <Toast
          bg="success"
          onClose={() => setShowCopyRecordToast(false)}
          show={showCopyRecordToast}
          delay={2000}
          autohide
        >
          <Toast.Body className="text-white">
            {translate(language, "PAGES.MEETING.MANAGE_MEETING.INFORMATION.RECORD_LINK.COPY_MESSAGE")}
          </Toast.Body>
        </Toast>

        <Toast
          bg="success"
          onClose={() => setShowCopyEmailsToast(false)}
          show={showCopyEmailsToast}
          delay={2000}
          autohide
        >
          <Toast.Body className="text-white">
            {translate(language, "PAGES.MEETING.MANAGE_MEETING.ATTENDANCE.ALERTS.SUCCESS.COPY_EMAILS")}
          </Toast.Body>
        </Toast>

        <Toast
          bg="success"
          show={showSendIcsSuccessToast}
          onClose={() => setShowSendIcsSuccessToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">
            {translate(language, "PAGES.MEETING.MANAGE_MEETING.ALERTS.SEND_ICS_ANIMATORS_SUCCESS")}
          </Toast.Body>
        </Toast>
      </ToastContainer>
      <MeetingReminderPopup
        show={showReminderPopup}
        onClose={() => setShowReminderPopup(false)}
        meeting={meeting}
        language={language}
      />
      <DstAlert show={showAlert} onClose={() => setShowAlert(false)} {...alertProps} />
    </div>
  );
};

export default ManageMeeting;
